import { reduce } from 'lodash-es'
import { Field } from '@app-types/entity_types'
import { getRowValueForField } from '@utils/row'
import { getPhotoIdFromKey } from '@utils/photo'
import { DerivedTimelineItems, DerivedMarker, DerivedTimespan } from '../types/derived_items'

export interface TimelineDerivationContext {
  rawData: any // rows array OR { markers, timespans }
  fields?: Field[]
  viewOptions?: any
}

export type TimelineDeriveFn = (ctx: TimelineDerivationContext) => DerivedTimelineItems

// Pass-through strategy for classic data already in marker/timespan shape
export const passThroughDerive: TimelineDeriveFn = ctx => {
  const { rawData } = ctx
  if (!rawData) return { markers: [], timespans: [] }
  const markers = Array.isArray(rawData.markers) ? rawData.markers.map((m: any) => ({ ...m, sourceRef: m })) : []
  const timespans = Array.isArray(rawData.timespans) ? rawData.timespans.map((t: any) => ({ ...t, sourceRef: t })) : []
  return { markers, timespans }
}

// Rows-based derivation (unbound)
export const fromRowsDerive: TimelineDeriveFn = ctx => {
  const { rawData: rows, fields = [], viewOptions } = ctx
  if (!Array.isArray(rows)) return { markers: [], timespans: [] }

  const findById = (id?: number) => (id ? fields.find(f => Number(f.id) === id) || null : null)
  const timelineField =
    findById(viewOptions?.date_field_id) ||
    fields.find(f => f.attributes.name === 'Timeline') ||
    fields.find(f => f.attributes.variant_type === 'TimelineField') ||
    null
  const titleField =
    findById(viewOptions?.title_field_id) ||
    fields.find(f => f.attributes.name === 'Title') ||
    fields.find(f => f.attributes.variant_type === 'TextField') ||
    null
  const descriptionField =
    findById(viewOptions?.description_field_id) ||
    fields.find(f => f.attributes.name === 'Description') ||
    fields.filter(f => f.attributes.variant_type === 'TextField').find(f => f.id !== titleField?.id) ||
    fields.filter(f => f.attributes.variant_type === 'RichTextField').find(f => f.id !== titleField?.id) ||
    null
  const photoField =
    findById(viewOptions?.multimedia_field_id) ||
    fields.find(f => f.attributes.name === 'Photo') ||
    fields.find(f => f.attributes.variant_type === 'PhotoField') ||
    null

  if (!timelineField) return { markers: [], timespans: [] }

  return reduce(
    rows,
    (acc: DerivedTimelineItems, row: any) => {
      const timelineValue: any = getRowValueForField(row, timelineField)
      if (!timelineValue) return acc
      const { s, e } = timelineValue
      if (!(s || e)) return acc
      const id = row.id
      const title = titleField ? (getRowValueForField(row, titleField) as string) || '' : ''
      const description = descriptionField ? (getRowValueForField(row, descriptionField) as string) || '' : ''
      const start = s || e
      const end = e || s
      let photo_ids: (string | number)[] = []
      if (photoField) {
        const pv = getRowValueForField(row, photoField)
        const pid = getPhotoIdFromKey(pv)
        if (pid !== null && pid !== undefined) photo_ids = [pid]
      }
      if (start && end && start !== end) {
        const timespan: DerivedTimespan = { id, title, description, start_date: start, end_date: end, sourceRef: row }
        acc.timespans.push(timespan)
      } else {
        const marker: DerivedMarker = { id, title, description, date: start || end || null, photo_ids, sourceRef: row }
        acc.markers.push(marker)
      }
      return acc
    },
    { markers: [], timespans: [] }
  )
}

export default {
  passThroughDerive,
  fromRowsDerive,
}
